import { SCALES, type ScaleName } from './scales';

export type MelodicContour = 'ascending' | 'descending' | 'arch' | 'wave' | 'random' | 'stepwise' | 'leapy';

/**
 * MarkovMelody - Generates melodic sequences using Markov chains
 * with advanced contour control and phrase awareness
 */
export class MarkovMelody {
  private scale: number[]; // MIDI note numbers
  private transitions: Map<number, Map<number, number>>;
  private currentNote: number;
  private contour: MelodicContour = 'random';
  private phrasePosition = 0;
  private phraseLength = 8;
  private momentum = 0; // -1 to 1, affects direction tendency
  private lastInterval = 0;
  private repeatCount = 0;
  private maxRepeats = 2;

  constructor(rootNote: number, scaleName: ScaleName, octaveRange = 2) {

    // Build scale notes across octave range
    this.scale = this.buildScale(rootNote, scaleName, octaveRange);

    // Build transition probability matrix
    this.transitions = this.buildTransitions();

    // Start on root note
    this.currentNote = this.scale[Math.floor(this.scale.length / 2)]; // Start in middle of range
  }

  /**
   * Build scale notes across octave range
   */
  private buildScale(
    rootNote: number,
    scaleName: ScaleName,
    octaveRange: number
  ): number[] {
    const scaleIntervals = SCALES[scaleName];
    const notes: number[] = [];

    for (let octave = -octaveRange; octave <= octaveRange; octave++) {
      for (const interval of scaleIntervals) {
        notes.push(rootNote + octave * 12 + interval);
      }
    }

    return notes.sort((a, b) => a - b);
  }

  /**
   * Build transition probability matrix
   * Now with more sophisticated probability distribution
   */
  private buildTransitions(): Map<number, Map<number, number>> {
    const trans = new Map<number, Map<number, number>>();

    this.scale.forEach((note, i) => {
      const noteProbs = new Map<number, number>();
      let totalWeight = 0;

      this.scale.forEach((target, j) => {
        const interval = Math.abs(j - i);
        // More nuanced probability based on interval
        let weight: number;
        
        if (interval === 0) {
          weight = 0.08; // Repeated note
        } else if (interval === 1) {
          weight = 0.35; // Stepwise - most common
        } else if (interval === 2) {
          weight = 0.25; // Third
        } else if (interval === 3) {
          weight = 0.12; // Fourth
        } else if (interval === 4) {
          weight = 0.08; // Fifth
        } else if (interval === 5) {
          weight = 0.05; // Sixth
        } else if (interval === 6) {
          weight = 0.04; // Seventh
        } else {
          weight = 0.03; // Octave or larger
        }

        totalWeight += weight;
        noteProbs.set(target, weight);
      });

      // Normalize probabilities
      noteProbs.forEach((weight, target) => {
        noteProbs.set(target, weight / totalWeight);
      });

      trans.set(note, noteProbs);
    });

    return trans;
  }

  /**
   * Set melodic contour for phrase shaping
   */
  setContour(contour: MelodicContour): void {
    this.contour = contour;
    this.phrasePosition = 0;
  }

  /**
   * Randomize contour for variety
   */
  randomizeContour(): void {
    const contours: MelodicContour[] = ['ascending', 'descending', 'arch', 'wave', 'random', 'stepwise', 'leapy'];
    this.contour = contours[Math.floor(Math.random() * contours.length)];
    this.phrasePosition = 0;
    this.phraseLength = 4 + Math.floor(Math.random() * 12); // 4-16 notes per phrase
  }

  /**
   * Get direction bias based on contour and phrase position
   */
  private getDirectionBias(): number {
    const progress = this.phrasePosition / this.phraseLength;

    switch (this.contour) {
      case 'ascending':
        return 0.7; // Strong upward bias
      case 'descending':
        return -0.7; // Strong downward bias
      case 'arch':
        // Up first half, down second half
        return progress < 0.5 ? 0.6 : -0.6;
      case 'wave':
        // Sinusoidal contour
        return Math.sin(progress * Math.PI * 2) * 0.5;
      case 'stepwise':
        return 0; // No bias, but interval filtering will ensure steps
      case 'leapy':
        return (Math.random() - 0.5) * 1.5; // Large random swings
      case 'random':
      default:
        return (Math.random() - 0.5) * 0.4; // Slight random bias
    }
  }

  /**
   * Get next note in sequence with contour awareness
   */
  nextNote(): number {
    const probs = this.transitions.get(this.currentNote);
    if (!probs) {
      this.currentNote = this.scale[Math.floor(this.scale.length / 2)];
      return this.currentNote;
    }

    // Get direction bias from contour
    const directionBias = this.getDirectionBias();
    
    // Apply momentum (tendency to continue in same direction)
    const effectiveBias = directionBias * 0.6 + this.momentum * 0.4;

    // Create adjusted probabilities based on contour
    const adjustedProbs = new Map<number, number>();
    let totalProb = 0;

    const currentIndex = this.scale.indexOf(this.currentNote);

    probs.forEach((prob, target) => {
      const targetIndex = this.scale.indexOf(target);
      const direction = targetIndex - currentIndex;
      const interval = Math.abs(direction);
      
      let adjustedProb = prob;

      // Avoid too many repeated notes
      if (target === this.currentNote) {
        if (this.repeatCount >= this.maxRepeats) {
          adjustedProb *= 0.1;
        }
      }

      // Apply direction bias
      if (effectiveBias > 0 && direction > 0) {
        adjustedProb *= 1 + effectiveBias;
      } else if (effectiveBias < 0 && direction < 0) {
        adjustedProb *= 1 + Math.abs(effectiveBias);
      }

      // Contour-specific interval adjustments
      if (this.contour === 'stepwise') {
        if (interval > 2) adjustedProb *= 0.2;
      } else if (this.contour === 'leapy') {
        if (interval > 2) adjustedProb *= 1.5;
        if (interval < 2) adjustedProb *= 0.5;
      }

      // Slight preference for contrary motion after large leaps
      if (Math.abs(this.lastInterval) > 3) {
        const contraryMotion = Math.sign(direction) !== Math.sign(this.lastInterval);
        if (contraryMotion) adjustedProb *= 1.3;
      }

      // Avoid extreme ranges (pull back from edges)
      if (targetIndex < 3 || targetIndex > this.scale.length - 4) {
        const distanceFromCenter = Math.abs(targetIndex - this.scale.length / 2);
        adjustedProb *= Math.max(0.3, 1 - distanceFromCenter / this.scale.length);
      }

      adjustedProbs.set(target, adjustedProb);
      totalProb += adjustedProb;
    });

    // Normalize and select
    const newNote = this.weightedRandom(adjustedProbs, totalProb);
    
    // Update state
    const newIndex = this.scale.indexOf(newNote);
    this.lastInterval = newIndex - currentIndex;
    
    if (newNote === this.currentNote) {
      this.repeatCount++;
    } else {
      this.repeatCount = 0;
    }
    
    // Update momentum based on recent movement
    this.momentum = this.momentum * 0.7 + (this.lastInterval > 0 ? 0.3 : -0.3);
    this.momentum = Math.max(-1, Math.min(1, this.momentum));

    this.currentNote = newNote;
    this.phrasePosition++;

    // Reset phrase periodically and possibly change contour
    if (this.phrasePosition >= this.phraseLength) {
      this.phrasePosition = 0;
      if (Math.random() < 0.3) {
        this.randomizeContour();
      }
    }

    return this.currentNote;
  }

  /**
   * Weighted random selection with total provided
   */
  private weightedRandom(probs: Map<number, number>, total: number): number {
    const rand = Math.random() * total;
    let sum = 0;

    for (const [note, prob] of probs) {
      sum += prob;
      if (rand < sum) {
        return note;
      }
    }

    // Fallback to random scale note
    return this.scale[Math.floor(Math.random() * this.scale.length)];
  }

  /**
   * Get current note
   */
  getCurrentNote(): number {
    return this.currentNote;
  }

  /**
   * Reset to root note
   */
  reset(): void {
    this.currentNote = this.scale[Math.floor(this.scale.length / 2)];
    this.phrasePosition = 0;
    this.momentum = 0;
    this.lastInterval = 0;
    this.repeatCount = 0;
  }

  /**
   * Change scale/root (regenerate)
   */
  changeScale(rootNote: number, scaleName: ScaleName): void {
    this.scale = this.buildScale(rootNote, scaleName, 2);
    this.transitions = this.buildTransitions();
    this.reset();
    this.randomizeContour();
  }
}
